import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL from '../config/api';
import {
  Box,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Button,
  Alert,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  MenuBook as MenuBookIcon,
  Assignment as AssignmentIcon,
  Forum as ForumIcon,
  Send as SendIcon,
  EditNote as EditNoteIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { fetchWithAuth } from '../utils/auth';
import ChatInterface from '../components/features/AIAssistant/ChatInterface';
import { getButtonStyles, BUTTON_COLORS } from '../config/buttonStyles';
import { useTheme } from '../utils/ThemeContext';
import { useInteractiveTutorial } from '../utils/InteractiveTutorialContext';
import '../assets/css/futuristic-modern.css';

const AIResearchAssistant = () => {
  const { isDarkMode } = useTheme();
  const { checkUnlockConditions } = useInteractiveTutorial();
  const [articles, setArticles] = useState({});
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [suggestions, setSuggestions] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [modelInfo, setModelInfo] = useState({});
  const [chatHistory, setChatHistory] = useState([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedConversationForMenu, setSelectedConversationForMenu] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newConversationTitle, setNewConversationTitle] = useState('');
  const [renamingConversation, setRenamingConversation] = useState(null);
  const [selectedQueries, setSelectedQueries] = useState(new Set()); // Track which queries have all articles selected
  const [sharedConversations, setSharedConversations] = useState([]);
  const [selectedSharedConversation, setSelectedSharedConversation] = useState(null);
  const [pendingSharedConversationId, setPendingSharedConversationId] = useState(null);
  const [pendingConversationId, setPendingConversationId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [hideNoArticles, setHideNoArticles] = useState(true); // Default: show only "yes" articles
  const [queryFilterStates, setQueryFilterStates] = useState({}); // Track filter state per query
  
  // Sharing states
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [lastSharedUser, setLastSharedUser] = useState(() => {
    // Load last shared user from localStorage - only if authenticated
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return null;
      }
      
      const saved = localStorage.getItem('lastSharedUser');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error loading last shared user:', error);
      return null;
    }
  });
  const chatInterfaceRef = useRef(null);

  // Snackbar notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' // 'success', 'error', 'warning', 'info'
  });

  const showNotification = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  // Load articles and model info on component mount
  useEffect(() => {
    loadArticles();
    loadModelInfo();
    loadChatHistory();
    loadSharedConversations();
    
    // Check unlock conditions when page loads
    if (checkUnlockConditions) {
      checkUnlockConditions();
    }
    
    // Check for conversation parameters and store them for later use
    const urlParams = new URLSearchParams(window.location.search);
    const sharedConversationId = urlParams.get('shared');
    const regularConversationId = urlParams.get('conversation');
    
    if (sharedConversationId) {
      // Store the conversation ID to load it once shared conversations are loaded
      setPendingSharedConversationId(sharedConversationId);
    }
    
    if (regularConversationId) {
      // Store the conversation ID to load it once chat history is loaded
      setPendingConversationId(regularConversationId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load suggestions when selection changes
  useEffect(() => {
    loadSuggestions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArticles]);

  // Refresh on page focus (event-driven instead of polling)
  // This catches new shared conversations when user returns to page
  useEffect(() => {
    const handleFocus = () => {
      if (!document.hidden) {
        loadChatHistory();
        loadSharedConversations();
      }
    };
    
    // Refresh when user returns to this browser tab/window
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle pending shared conversation when sharedConversations are loaded
  useEffect(() => {
    if (pendingSharedConversationId && sharedConversations.length > 0) {
      const conversation = sharedConversations.find(conv => conv.id === pendingSharedConversationId);
      if (conversation) {
        handleSharedConversationClick(conversation);
        setPendingSharedConversationId(null); // Clear the pending ID
      }
    }
  }, [sharedConversations, pendingSharedConversationId]);

  // Handle pending regular conversation when chatHistory is loaded
  useEffect(() => {
    if (pendingConversationId && chatHistory.length > 0) {
      const conversation = chatHistory.find(conv => conv.id === pendingConversationId);
      if (conversation) {
        handleHistoryClick(conversation);
        setPendingConversationId(null); // Clear the pending ID
      }
    }
  }, [chatHistory, pendingConversationId]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth('/api/ai/articles');
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setArticles(data.articles_by_query || {});
      }
    } catch (err) {
      setError('Failed to load articles');
      console.error('Error loading articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadModelInfo = async () => {
    try {
      const response = await fetchWithAuth('/api/ai/model-info');
      const data = await response.json();
      setModelInfo(data);
    } catch (err) {
      console.error('Error loading model info:', err);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const loadSuggestions = async () => {
    try {
      const selectedIds = selectedArticles.map(article => article.id);
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/ai/suggestions?selected_article_ids=${selectedIds.join(',')}`
      );
      const data = await response.json();
      setSuggestions(data.suggestions || []);
    } catch (err) {
      console.error('Error loading suggestions:', err);
    }
  };

  const loadChatHistory = async () => {
    try {
      const response = await fetchWithAuth('/api/ai/conversation-history');
      const data = await response.json();
      setChatHistory(data.conversations || []);
    } catch (err) {
      console.error('Error loading chat history:', err);
    }
  };

  const loadSharedConversations = async () => {
    try {
      const response = await fetchWithAuth('/api/ai/shared-conversations');
      const data = await response.json();
      setSharedConversations(data.conversations || []);
    } catch (err) {
      console.error('Error loading shared conversations:', err);
    }
  };

  const handleSharedConversationClick = (conversation) => {
    setSelectedSharedConversation(conversation);
    setSelectedHistoryItem(null); // Clear regular history selection
    setCurrentConversationId(conversation.id);
    
    // Load the shared conversation messages into the chat interface
    if (chatInterfaceRef.current) {
      chatInterfaceRef.current.loadSharedConversation(conversation, true); // true = read-only
    }
  };


  const handleDeleteSharedConversation = (conversation, event) => {
    event.preventDefault();
    event.stopPropagation();
    setConversationToDelete(conversation);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteSharedConversation = async () => {
    if (!conversationToDelete) return;
    
    try {
      const response = await fetchWithAuth('/api/ai/delete-shared-conversation', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: conversationToDelete.id,
          shared_by_email: conversationToDelete.shared_by_email
        })
      });

      if (response.ok) {
        // Remove from local state
        setSharedConversations(prev => prev.filter(conv => conv.id !== conversationToDelete.id));
        
        // Clear selection if this was the selected conversation
        if (selectedSharedConversation?.id === conversationToDelete.id) {
          setSelectedSharedConversation(null);
          setCurrentConversationId(null);
          if (chatInterfaceRef.current) {
            chatInterfaceRef.current.startNewChat();
          }
        }
        
        console.log('✅ Shared conversation deleted successfully');
        showNotification('Shared conversation removed successfully', 'success');
      } else {
        const errorData = await response.json();
        console.error('❌ Error deleting shared conversation:', errorData.error);
        showNotification('Error removing shared conversation: ' + errorData.error, 'error');
      }
    } catch (error) {
      console.error('❌ Error deleting shared conversation:', error);
      showNotification('Error removing shared conversation: ' + error.message, 'error');
    } finally {
      setDeleteConfirmOpen(false);
      setConversationToDelete(null);
    }
  };

  // Check if knowledge base is locked (conversation is active)
  const isKnowledgeBaseLocked = currentConversationId !== null;

  const handleArticleToggle = (article) => {
    // Prevent changes if knowledge base is locked
    if (isKnowledgeBaseLocked) {
      console.log('🔒 Knowledge base is locked - cannot modify article selection');
      return;
    }
    
    setSelectedArticles(prev => {
      const isSelected = prev.some(selected => selected.id === article.id);
      if (isSelected) {
        // Allow deselection
        return prev.filter(selected => selected.id !== article.id);
      } else {
        // Check article limit (max 4 articles)
        if (prev.length >= 4) {
          showNotification('Maximum 4 articles can be selected at a time to ensure optimal AI performance.', 'warning');
          return prev; // Don't add more
        }
        return [...prev, article];
      }
    });
  };

  const handleSelectAll = () => {
    // Prevent changes if knowledge base is locked
    if (isKnowledgeBaseLocked) {
      console.log('🔒 Knowledge base is locked - cannot select all');
      return;
    }
    
    const allArticles = Object.values(articles).flat();
    
    // Limit to first 4 articles
    if (allArticles.length > 4) {
      showNotification(`You have ${allArticles.length} articles. Selecting the first 4 (maximum limit for optimal AI performance).`, 'info');
      setSelectedArticles(allArticles.slice(0, 4));
    } else {
      setSelectedArticles(allArticles);
    }
    setSelectedQueries(new Set(Object.keys(articles)));
  };

  const handleClearSelection = () => {
    // Prevent changes if knowledge base is locked
    if (isKnowledgeBaseLocked) {
      console.log('🔒 Knowledge base is locked - cannot clear selection');
      return;
    }
    
    setSelectedArticles([]);
    setSelectedQueries(new Set());
  };

  // eslint-disable-next-line no-unused-vars
  const handleSelectQuery = (queryName) => {
    // Prevent changes if knowledge base is locked
    if (isKnowledgeBaseLocked) {
      console.log('🔒 Knowledge base is locked - cannot modify query selection');
      return;
    }
    
    const queryArticles = articles[queryName] || [];
    const isQuerySelected = selectedQueries.has(queryName);
    
    if (isQuerySelected) {
      // Unselect all articles from this query
      setSelectedArticles(prev => {
        const queryArticleIds = new Set(queryArticles.map(article => article.id));
        return prev.filter(article => !queryArticleIds.has(article.id));
      });
      setSelectedQueries(prev => {
        const newSet = new Set(prev);
        newSet.delete(queryName);
        return newSet;
      });
    } else {
      // Select all articles from this query (respecting 4-article limit)
      setSelectedArticles(prev => {
        const existingIds = new Set(prev.map(article => article.id));
        const newArticles = queryArticles.filter(article => !existingIds.has(article.id));
        
        // Check if adding these would exceed the limit
        const wouldExceedLimit = prev.length + newArticles.length > 4;
        
        if (wouldExceedLimit) {
          const available = 4 - prev.length;
          if (available <= 0) {
            showNotification('Maximum 4 articles can be selected. Please deselect some articles first.', 'warning');
            return prev;
          }
          showNotification(`Can only add ${available} more article(s) to reach the maximum of 4. Adding first ${available} from this query.`, 'info');
          return [...prev, ...newArticles.slice(0, available)];
        }
        
        return [...prev, ...newArticles];
      });
      setSelectedQueries(prev => {
        const newSet = new Set(prev);
        newSet.add(queryName);
        return newSet;
      });
    }
  };

  const handleNewChat = () => {
    setSelectedHistoryItem(null);
    setCurrentConversationId(null);
    if (chatInterfaceRef.current) {
      chatInterfaceRef.current.startNewChat();
    }
    setTimeout(() => {
      loadChatHistory();
    }, 100);
  };

  const handleHistoryClick = (conversation) => {
    setSelectedHistoryItem(conversation);
    setCurrentConversationId(conversation.id);
  };

  const handleMenuOpen = (event, conversation) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedConversationForMenu(conversation);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedConversationForMenu(null);
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversationForMenu) return;
    
    try {
      await fetchWithAuth(`/api/ai/delete-conversation/${selectedConversationForMenu.id}`, {
        method: 'DELETE'
      });
      
      // Remove from local state
      setChatHistory(prev => prev.filter(conv => conv.id !== selectedConversationForMenu.id));
      
      // If this was the currently selected conversation, clear it
      if (selectedHistoryItem?.id === selectedConversationForMenu.id) {
        setSelectedHistoryItem(null);
        setCurrentConversationId(null);
        if (chatInterfaceRef.current) {
          chatInterfaceRef.current.startNewChat();
        }
      }
      
      handleMenuClose();
    } catch (err) {
      console.error('Error deleting conversation:', err);
    }
  };

  const handleRenameConversation = (conversation) => {
    if (!conversation) {
      console.error('❌ No conversation provided for renaming');
      return;
    }
    console.log('✏️ Renaming conversation:', conversation);
    setRenamingConversation(conversation);
    setNewConversationTitle(conversation.preview || '');
    setRenameDialogOpen(true);
    
    // Close menu immediately for better UX
    setMenuAnchorEl(null);
    setSelectedConversationForMenu(null);
  };

  const loadUsers = async () => {
    try {
      const response = await fetchWithAuth('/api/collaboration/users');
      const data = await response.json();
      
      if (data.error) {
        console.error('Error loading users:', data.error);
      } else {
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleShareConversation = (conversation) => {
    if (!conversation) {
      console.error('❌ No conversation provided for sharing');
      return;
    }
    console.log('📤 Sharing conversation:', conversation);
    
    // Close menu immediately for better UX
    setMenuAnchorEl(null);
    setSelectedConversationForMenu(null);
    
    // Set the conversation to share and open the share dialog
    setSelectedConversationForMenu(conversation);
    setShareDialogOpen(true);
    loadUsers(); // Load users for selection
  };

  const handleShareToUser = async () => {
    if (!selectedUser || !selectedConversationForMenu) return;
    
    try {
      setSending(true);
      
      const response = await fetchWithAuth('/api/collaboration/share-items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_id: selectedUser.id,
          items: [{
            type: 'ai_conversation',
            id: selectedConversationForMenu.id,
            title: selectedConversationForMenu.title || selectedConversationForMenu.preview || 'AI Conversation',
            preview: selectedConversationForMenu.preview || selectedConversationForMenu.title || 'AI Conversation',
            content: selectedConversationForMenu.preview || selectedConversationForMenu.title || 'AI Conversation shared with you',
            date: selectedConversationForMenu.date || new Date().toISOString(),
            message_count: selectedConversationForMenu.message_count || 0,
          }],
          share_type: 'ai_conversation',
          message: shareMessage.trim() || `Shared AI conversation with you`
        })
      });
      
      const data = await response.json();
      
      if (data.error) {
        showNotification('Error sharing conversation: ' + data.error, 'error');
      } else {
        showNotification('Conversation shared successfully!', 'success');
        // Track the last shared user and save to localStorage
        setLastSharedUser(selectedUser);
        try {
          localStorage.setItem('lastSharedUser', JSON.stringify(selectedUser));
        } catch (error) {
          console.error('Error saving last shared user:', error);
        }
        
        // Emit event to update collaboration badge in navigation
        window.dispatchEvent(new CustomEvent('collaborationMessageSent'));
        
        setShareDialogOpen(false);
        setSelectedUser(null);
        setShareMessage('');
        setUserSearchQuery('');
        setSelectedConversationForMenu(null);
      }
    } catch (err) {
      console.error('Error sharing conversation:', err);
      showNotification('Error sharing conversation: ' + err.message, 'error');
    } finally {
      setSending(false);
    }
  };

  const handleSaveRename = () => {
    if (!newConversationTitle.trim() || !renamingConversation) {
      return;
    }

    // Store values before clearing state
    const newTitle = newConversationTitle.trim();
    const conversationId = renamingConversation.id;
    const oldTitle = renamingConversation.preview || 'Conversation';
    
    // Close dialog immediately
    setRenameDialogOpen(false);
    setNewConversationTitle('');
    setRenamingConversation(null);

    // Update local state immediately
    setChatHistory(prev => prev.map(conv => 
      conv.id === conversationId 
        ? { ...conv, preview: newTitle }
        : conv
    ));
    
    // Update selected conversation if it's the current one
    if (selectedHistoryItem?.id === conversationId) {
      setSelectedHistoryItem(prev => ({
        ...prev,
        preview: newTitle
      }));
    }

    // Make API call in background without blocking UI
    setTimeout(() => {
      fetchWithAuth(`/api/ai/rename-conversation/${conversationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: newTitle
        })
      })
      .then(response => {
        if (response.ok) {
          console.log('✏️ Conversation renamed successfully');
        } else {
          console.error('❌ Failed to rename conversation');
          // Revert the optimistic update on error
          setChatHistory(prev => prev.map(conv => 
            conv.id === conversationId 
              ? { ...conv, preview: oldTitle }
              : conv
          ));
          if (selectedHistoryItem?.id === conversationId) {
            setSelectedHistoryItem(prev => ({
              ...prev,
              preview: oldTitle
            }));
          }
        }
      })
      .catch(err => {
        console.error('❌ Error renaming conversation:', err);
        // Revert the optimistic update on error
        setChatHistory(prev => prev.map(conv => 
          conv.id === conversationId 
            ? { ...conv, preview: oldTitle }
            : conv
        ));
        if (selectedHistoryItem?.id === conversationId) {
          setSelectedHistoryItem(prev => ({
            ...prev,
            preview: oldTitle
          }));
        }
      });
    }, 0);
  };

  const handleCancelRename = () => {
    setRenameDialogOpen(false);
    setNewConversationTitle('');
    setRenamingConversation(null);
    // Clear menu state when canceling
    setMenuAnchorEl(null);
    setSelectedConversationForMenu(null);
  };

  const handleMessageSent = () => {
    console.log('📨 Message sent, refreshing chat history...');
    // Simple refresh - backend should return saved conversations immediately
    setTimeout(() => {
      loadChatHistory();
    }, 500);
    
    // Check if creating a conversation unlocks Collaboration tutorial
    if (checkUnlockConditions) {
      checkUnlockConditions();
    }
  };



  const getTotalArticles = () => {
    return Object.values(articles || {}).reduce((total, queryArticles) => total + (queryArticles || []).length, 0);
  };

  const getFilteredArticles = (queryArticles, queryName) => {
    // Check if this query has a specific filter state, otherwise use global
    const shouldFilter = queryFilterStates[queryName] !== undefined 
      ? queryFilterStates[queryName] 
      : hideNoArticles;
    
    if (!shouldFilter) {
      return queryArticles; // Show all articles
    }
    
    // Filter to show only articles classified as "yes" by AI
    const filtered = queryArticles.filter(article => {
      const prediction = article.ai_prediction || article.prediction || article.user_classification;
      return prediction === 'yes';
    });
    
    return filtered;
  };

  const getVisibleArticlesCount = (queryArticles, queryName) => {
    const filtered = getFilteredArticles(queryArticles, queryName);
    return filtered.length;
  };

  const toggleQueryFilter = (queryName) => {
    setQueryFilterStates(prev => ({
      ...prev,
      [queryName]: !(prev[queryName] !== undefined ? prev[queryName] : hideNoArticles)
    }));
  };

  const isQueryFiltered = (queryName) => {
    return queryFilterStates[queryName] !== undefined 
      ? queryFilterStates[queryName] 
      : hideNoArticles;
  };

  const formatHistoryDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };



  return (
    <>
      <div style={{ 
        height: 'calc(100vh - 90px)', 
        padding: '1rem 2.7rem 2rem 1rem', // top right bottom left 
        background: 'var(--surface)', 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 80px)',
        minHeight: 'calc(100vh - 80px)'
      }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <div className="loading-spinner-modern"></div>
          </Box>
        ) : error ? (
          <Alert 
            severity="error" 
            className="modern-card"
            sx={{ 
              backgroundColor: '#ffebee !important',
              color: '#000000 !important',
              '& .MuiAlert-icon': {
                color: '#c62828 !important'
              },
              border: '1px solid #ef5350'
            }}
          >
            {error}
          </Alert>
        ) : (
          <>

            
            <Grid container spacing={2} style={{ height: '100%', overflow: 'hidden', margin: '0 3rem 0 0' }}>
              {/* Left Panel - Articles Selection */}
              <Grid item xs={12} md={4} style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Selected Articles Section */}
              <div className="modern-card" style={{ flex: '0 0 280px', marginBottom: '1rem', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} data-tutorial="selected-articles-section">
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} style={{ flexShrink: 0 }}>
                  <Typography variant="h6" className="text-neon" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MenuBookIcon style={{ fontSize: '1.2rem', color: '#6c3082' }} />
                    Selected Articles ({(selectedArticles || []).length}/4)
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button 
                      size="small" 
                      onClick={handleSelectAll}
                      disabled={isKnowledgeBaseLocked}
                      className="btn-modern"
                      data-tutorial="select-all-button"
                      sx={{ 
                        ...getButtonStyles(false, isDarkMode, 'generic'),
                        borderRadius: 'var(--radius-md)',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        padding: '4px 12px'
                      }}
                    >
                      Select All
                    </Button>
                    <Button 
                      size="small" 
                      onClick={handleClearSelection}
                      disabled={isKnowledgeBaseLocked}
                      className="btn-modern"
                      data-tutorial="clear-button"
                      sx={{ 
                        ...getButtonStyles(false, isDarkMode, 'generic'),
                        borderRadius: 'var(--radius-md)',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        padding: '4px 12px'
                      }}
                    >
                      Clear
                    </Button>
                    {isKnowledgeBaseLocked && (
                      <Chip 
                        icon={<LockIcon style={{ fontSize: '0.75rem' }} />}
                        label="Locked"
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255, 152, 0, 0.2)',
                          border: '1px solid rgba(255, 152, 0, 0.5)',
                          color: '#ff9800',
                          fontSize: '0.75rem',
                          height: '24px'
                        }}
                      />
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  {(selectedArticles || []).length === 0 ? (
                    <Typography variant="body2" style={{ color: 'var(--text-muted)' }}>
                      No articles selected. Select articles to ask specific questions, or leave empty to analyze all articles.
                    </Typography>
                  ) : (
                    <List dense>
                      {(selectedArticles || []).map((article, index) => (
                        <ListItem 
                          key={article.id}
                          onClick={() => !isKnowledgeBaseLocked && handleArticleToggle(article)}
                          sx={{ 
                            py: 0.5,
                            cursor: isKnowledgeBaseLocked ? 'not-allowed' : 'pointer',
                            borderRadius: 'var(--radius-md)',
                            mb: 0.5,
                            transition: 'all 0.2s ease',
                            backgroundColor: 'rgba(70, 130, 48, 0.15)',
                            border: '1px solid rgba(70, 130, 48, 0.5)',
                            '&:hover': {
                              backgroundColor: 'rgba(70, 130, 48, 0.2)',
                              borderColor: 'rgba(70, 130, 48, 0.7)',
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              size="small"
                              checked={true}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleArticleToggle(article);
                              }}
                              disabled={isKnowledgeBaseLocked}
                              icon={<Box sx={{ 
                                width: 20, 
                                height: 20, 
                                borderRadius: '50%', 
                                border: '2px solid var(--text-muted)',
                                backgroundColor: 'transparent'
                              }} />}
                              checkedIcon={<Box sx={{ 
                                width: 20, 
                                height: 20, 
                                borderRadius: '50%', 
                                border: '2px solid #468230',
                                backgroundColor: '#468230',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#ffffff',
                                fontSize: '14px',
                                fontWeight: 'bold'
                              }}>✓</Box>}
                              sx={{
                                color: 'var(--text-muted)',
                                '&.Mui-checked': {
                                  color: '#468230',
                                },
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={article.title}
                            secondary={
                              <div>
                                <div>{`${article.authors} (${article.year})`}</div>
                                {article.abstract && (
                                  <div style={{ 
                                    fontSize: '0.7rem', 
                                    color: 'var(--text-muted)',
                                    marginTop: '0.25rem',
                                    lineHeight: '1.3'
                                  }}>
                                    {article.abstract.length > 150 
                                      ? `${article.abstract.substring(0, 150)}...` 
                                      : article.abstract
                                    }
                                  </div>
                                )}
                              </div>
                            }
                            primaryTypographyProps={{ 
                              fontSize: '0.875rem',
                              style: { color: 'var(--text-primary)' }
                            }}
                            secondaryTypographyProps={{ 
                              fontSize: '0.75rem',
                              style: { color: 'var(--text-muted)' }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </div>

              {/* All Queries Section */}
              <div className="modern-card" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Typography variant="h6" className="text-neon" sx={{ mb: 2, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon style={{ fontSize: '1.2rem', color: '#6c3082' }} />
                  All Queries ({Object.keys(articles || {}).length})
                </Typography>
                
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, maxHeight: '100%' }}>
                  {Object.entries(articles || {}).map(([queryName, queryArticles]) => (
                    <Accordion 
                      key={queryName}
                      data-tutorial="query-accordion"
                      defaultExpanded={false} 
                      sx={{ 
                        mb: 1,
                        boxShadow: 'none',
                        backgroundColor: 'transparent',
                        '&:before': {
                          display: 'none',
                        },
                        '& .MuiAccordionSummary-root': {
                          backgroundColor: 'var(--surface-2)',
                          border: '1px solid var(--border)',
                          borderRadius: '4px',
                          margin: 0,
                          minHeight: '48px',
                          '&:hover': {
                            borderColor: 'var(--border-strong)',
                          },
                          '&.Mui-expanded': {
                            minHeight: '48px',
                            borderRadius: '4px 4px 0 0',
                          }
                        },
                        '& .MuiAccordionDetails-root': {
                          backgroundColor: 'var(--surface-2)',
                          border: '1px solid var(--border)',
                          borderTop: 'none',
                          borderRadius: '0 0 4px 4px',
                          margin: 0,
                          padding: '0.5rem'
                        },
                        '& .MuiAccordionSummary-content': {
                          margin: '8px 0',
                          '&.Mui-expanded': {
                            margin: '8px 0',
                          }
                        }
                      }}
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon style={{ color: 'var(--text-primary)' }} />}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                  <Typography variant="subtitle1" style={{ color: 'var(--text-primary)' }}>
                          {queryName}
                        </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip 
                              label={isQueryFiltered(queryName)
                                ? `${getVisibleArticlesCount(queryArticles, queryName)}/${(queryArticles || []).length}`
                                : (queryArticles || []).length
                              }
                              size="small"
                              className="btn-modern-secondary"
                              sx={{
                                backgroundColor: isQueryFiltered(queryName) ? 'rgba(70, 130, 48, 0.2)' : 'rgba(108, 48, 130, 0.2)',
                                border: isQueryFiltered(queryName) ? '1px solid rgba(70, 130, 48, 0.5)' : '1px solid rgba(108, 48, 130, 0.5)',
                                color: isQueryFiltered(queryName) ? '#468230' : '#6c3082'
                              }}
                            />
                            <Button 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleQueryFilter(queryName);
                              }}
                              className="btn-modern"
                              data-tutorial="ai-filter-toggle"
                              startIcon={isQueryFiltered(queryName) ? <VisibilityIcon /> : <VisibilityOffIcon />}
                              sx={{ 
                                ...getButtonStyles(false, isDarkMode, 'generic'),
                                borderRadius: 'var(--radius-md)',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                padding: '4px 12px'
                              }}
                            >
                              {isQueryFiltered(queryName) ? 'Show All' : 'AI Filtered'}
                            </Button>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense sx={{ maxHeight: '200px', overflow: 'auto' }}>
                          {getFilteredArticles(queryArticles || [], queryName).length === 0 ? (
                            <ListItem>
                              <ListItemText
                                primary={isQueryFiltered(queryName)
                                  ? "No articles classified as 'yes' by AI"
                                  : "No articles available"
                                }
                                primaryTypographyProps={{
                                  fontSize: '0.875rem',
                                  style: { 
                                    color: 'var(--text-muted)',
                                    fontStyle: 'italic',
                                    textAlign: 'center'
                                  }
                                }}
                              />
                            </ListItem>
                          ) : (
                            getFilteredArticles(queryArticles || [], queryName).map((article) => {
                              const isSelected = (selectedArticles || []).some(selected => selected.id === article.id);
                              return (
                              <ListItem 
                                key={article.id} 
                                onClick={() => !isKnowledgeBaseLocked && handleArticleToggle(article)}
                                sx={{ 
                                  py: 0.5,
                                  cursor: isKnowledgeBaseLocked ? 'not-allowed' : 'pointer',
                                  borderRadius: 'var(--radius-md)',
                                  mb: 0.5,
                                  transition: 'all 0.2s ease',
                                  backgroundColor: isSelected 
                                    ? 'rgba(70, 130, 48, 0.15)' 
                                    : 'transparent',
                                  border: isSelected
                                    ? '1px solid rgba(70, 130, 48, 0.5)'
                                    : '1px solid transparent',
                                  '&:hover': {
                                    backgroundColor: isSelected
                                      ? 'rgba(70, 130, 48, 0.2)'
                                      : 'rgba(108, 48, 130, 0.1)',
                                    borderColor: isSelected
                                      ? 'rgba(70, 130, 48, 0.7)'
                                      : 'rgba(108, 48, 130, 0.3)',
                                  }
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Checkbox
                                    size="small"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleArticleToggle(article);
                                    }}
                                    disabled={isKnowledgeBaseLocked}
                                    data-tutorial="article-checkbox"
                                    icon={<Box sx={{ 
                                      width: 20, 
                                      height: 20, 
                                      borderRadius: '50%', 
                                      border: '2px solid var(--text-muted)',
                                      backgroundColor: 'transparent'
                                    }} />}
                                    checkedIcon={<Box sx={{ 
                                      width: 20, 
                                      height: 20, 
                                      borderRadius: '50%', 
                                      border: '2px solid #468230',
                                      backgroundColor: '#468230',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: '#ffffff',
                                      fontSize: '14px',
                                      fontWeight: 'bold'
                                    }}>✓</Box>}
                                    sx={{
                                      color: 'var(--text-muted)',
                                      '&.Mui-checked': {
                                        color: '#468230',
                                      },
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={article.title}
                                  secondary={
                                    <div>
                                      <div>{`${article.authors} (${article.year})`}</div>
                                      {article.abstract && (
                                        <div style={{ 
                                          fontSize: '0.7rem', 
                                          color: 'var(--text-muted)',
                                          marginTop: '0.25rem',
                                          lineHeight: '1.3'
                                        }}>
                                          {article.abstract.length > 150 
                                            ? `${article.abstract.substring(0, 150)}...` 
                                            : article.abstract
                                          }
                                        </div>
                                      )}
                                    </div>
                                  }
                                  primaryTypographyProps={{ 
                                    fontSize: '0.875rem',
                                    style: { color: 'var(--text-primary)' }
                                  }}
                                  secondaryTypographyProps={{ 
                                    fontSize: '0.75rem',
                                    style: { color: 'var(--text-muted)' }
                                  }}
                                />
                              </ListItem>
                            );
                            })
                          )}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </div>
            </Grid>

            {/* Middle Panel - Chat Interface */}
            <Grid item xs={12} md={5} style={{ height: '100%', overflow: 'hidden' }}>
              <div className="modern-card" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} data-tutorial="chat-interface">
                <ChatInterface 
                  ref={chatInterfaceRef}
                  selectedArticles={selectedArticles}
                  totalArticles={getTotalArticles()}
                  onMessageSent={handleMessageSent}
                  selectedHistoryItem={selectedHistoryItem}
                  currentConversationId={currentConversationId}
                />
              </div>
            </Grid>

            {/* Right Panel - Chat History & Shared Conversations */}
            <Grid item xs={12} md={3} style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Chat History Card */}
              <div className="modern-card" style={{ flex: '0 0 calc(50% - 0.5rem)', marginBottom: '1rem', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} data-tutorial="chat-history-section">
                {/* Header */}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <Typography variant="h6" className="text-neon" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ForumIcon style={{ fontSize: '1.2rem', color: '#6c3082' }} />
                    Chat History
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button 
                      size="small" 
                      onClick={handleNewChat}
                      className="btn-modern"
                      data-tutorial="new-chat-button"
                      startIcon={<AddIcon />}
                      sx={getButtonStyles(false, isDarkMode, 'generic')}
                    >
                      New Chat
                    </Button>
                  </Box>
                </Box>

                {/* Chat History List */}
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  {(chatHistory || []).length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" style={{ color: 'var(--text-muted)' }}>
                        No previous conversations
                      </Typography>
                    </Box>
                  ) : (
                    <List dense>
                      {(chatHistory || []).map((conversation, index) => (
                        <ListItem 
                          key={conversation.id} 
                          className="modern-card"
                          data-tutorial="conversation-item"
                          sx={{ 
                            py: 1, 
                            mb: 1,
                            cursor: 'pointer',
                            bgcolor: selectedHistoryItem?.id === conversation.id ? 'rgba(108, 48, 130, 0.1)' : 'transparent',
                            border: selectedHistoryItem?.id === conversation.id ? '1px solid #6c3082' : '1px solid var(--border)',
                            '&:hover': { 
                              borderColor: 'var(--border-strong)'
                            },
                            transition: 'border-color 0.2s ease'
                          }}
                          onClick={() => handleHistoryClick(conversation)}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              onClick={(e) => handleMenuOpen(e, conversation)}
                              size="small"
                              data-tutorial="conversation-menu-button"
                              sx={{
                                opacity: 1,
                                backgroundColor: 'rgba(108, 48, 130, 0.3) !important',
                                border: '1px solid rgba(108, 48, 130, 0.5) !important',
                                color: '#6c3082 !important',
                                minWidth: '24px',
                                width: '24px',
                                height: '24px',
                                borderRadius: 'var(--radius-sm)',
                                '&:hover': {
                                  opacity: '1 !important',
                                  backgroundColor: 'rgba(108, 48, 130, 0.5) !important',
                                  border: '1px solid rgba(108, 48, 130, 0.7) !important',
                                  transform: 'scale(1.05)',
                                  boxShadow: '0 2px 8px rgba(108, 48, 130, 0.4)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" noWrap style={{ color: 'var(--text-primary)' }}>
                                {conversation.preview || 'Conversation'}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
                                {formatHistoryDate(conversation.timestamp)}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </div>

              {/* Shared Conversations Card */}
              <div className="modern-card" style={{ flex: '0 0 calc(50% - 0.5rem)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} data-tutorial="shared-conversations-section">
                {/* Header */}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <Typography variant="h6" className="text-neon" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SendIcon style={{ fontSize: '1.2rem', color: '#6c3082' }} />
                    Shared Conversations
                  </Typography>
                </Box>

                {/* Shared Conversations List */}
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  {(sharedConversations || []).length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" style={{ color: 'var(--text-muted)' }}>
                        No shared conversations
                      </Typography>
                    </Box>
                  ) : (
                    <List dense>
                      {(sharedConversations || []).map((conversation, index) => (
                        <ListItem 
                          key={conversation.id} 
                          className="modern-card"
                          sx={{ 
                            py: 1, 
                            mb: 1,
                            cursor: 'pointer',
                            border: '1px solid var(--border)',
                            '&:hover': { 
                              borderColor: 'var(--border-strong)'
                            },
                            transition: 'border-color 0.2s ease'
                          }}
                          onClick={() => handleSharedConversationClick(conversation)}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" noWrap style={{ color: 'var(--text-primary)' }}>
                                {conversation.preview || 'Shared Conversation'}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
                                Shared by {conversation.shared_by} • {formatHistoryDate(conversation.timestamp)}
                              </Typography>
                            }
                          />
                          <IconButton
                            size="small"
                            className="btn-icon-small"
                            onClick={(e) => handleDeleteSharedConversation(conversation, e)}
                            sx={{ 
                              ml: 1,
                              color: 'var(--text-muted)',
                              '&:hover': {
                                color: '#ff4444',
                                backgroundColor: 'rgba(255, 68, 68, 0.1)'
                              }
                            }}
                            title="Remove shared conversation"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </div>
              
              {/* Menu for conversation actions */}
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    backgroundColor: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    boxShadow: 'none',
                    '& .MuiMenuItem-root': {
                      color: 'var(--text-primary)',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'transparent !important',
                      '&:hover': {
                        backgroundColor: 'transparent !important',
                      },
                      '&:active': {
                        backgroundColor: 'transparent !important',
                      },
                    },
                  }
                }}
              >
                <MenuItem 
                  onClick={() => handleRenameConversation(selectedConversationForMenu)}
                  sx={{
                    color: 'var(--text-primary)',
                    padding: '0.75rem 1rem',
                    borderRadius: 'var(--radius-sm)',
                    margin: '0.25rem',
                    backgroundColor: 'transparent !important',
                    border: '1px solid transparent',
                    '&:hover': {
                      backgroundColor: `${BUTTON_COLORS.hoverFill} !important`,
                      borderColor: BUTTON_COLORS.borderHover,
                      transform: 'none',
                    },
                    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
                  }}
                >
                  <EditIcon fontSize="small" sx={{ mr: 1, color: 'var(--text-primary)' }} />
                  Rename Conversation
                </MenuItem>
                <MenuItem 
                  onClick={() => handleShareConversation(selectedConversationForMenu)}
                  data-tutorial="share-conversation-button"
                  sx={{
                    color: 'var(--text-primary)',
                    padding: '0.75rem 1rem',
                    borderRadius: 'var(--radius-sm)',
                    margin: '0.25rem',
                    backgroundColor: 'transparent !important',
                    border: '1px solid transparent',
                    '&:hover': {
                      backgroundColor: `${BUTTON_COLORS.hoverFill} !important`,
                      borderColor: BUTTON_COLORS.borderHover,
                      transform: 'none',
                    },
                    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
                  }}
                >
                  <ShareIcon fontSize="small" sx={{ mr: 1, color: 'var(--text-primary)' }} />
                  Share with Colleagues
                </MenuItem>
                <MenuItem 
                  onClick={handleDeleteConversation}
                  sx={{
                    color: 'var(--text-primary)',
                    padding: '0.75rem 1rem',
                    borderRadius: 'var(--radius-sm)',
                    margin: '0.25rem',
                    backgroundColor: 'transparent !important',
                    border: '1px solid transparent',
                    '&:hover': {
                      backgroundColor: `${BUTTON_COLORS.hoverFill} !important`,
                      borderColor: BUTTON_COLORS.borderHover,
                      transform: 'none',
                    },
                    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
                  }}
                >
                  <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'var(--text-primary)' }} />
                  Delete Conversation
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>
        </>
        )}
      </div>

      {/* Rename Conversation Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={handleCancelRename}
        maxWidth="sm"
        fullWidth
        transitionDuration={200}
        PaperProps={{
          sx: {
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            backdropFilter: 'blur(10px)',
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            transform: 'translateZ(0)',
            willChange: 'transform',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(5px)',
          }
        }}
      >
        <DialogTitle sx={{
          color: 'var(--text-primary)',
          borderBottom: '1px solid var(--glass-border)',
          pb: 2
        }}>
          <Typography variant="h6" sx={{ color: 'var(--neon-cyan)', display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditNoteIcon style={{ fontSize: '1.2rem', color: '#6c3082' }} />
            Rename Conversation
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, pb: 2, flex: 1, display: 'flex', alignItems: 'center', minHeight: '120px' }}>
          <TextField
            fullWidth
            label="Conversation Title"
            value={newConversationTitle}
            onChange={(e) => setNewConversationTitle(e.target.value)}
            variant="outlined"
            autoFocus
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'var(--text-primary)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                minHeight: '60px',
                '& fieldset': {
                  borderColor: 'var(--glass-border)',
                },
                '&:hover fieldset': {
                  borderColor: 'var(--neon-cyan)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--neon-cyan)',
                },
                '& input': {
                  padding: '18px 16px',
                  fontSize: '1.1rem',
                  lineHeight: '1.5',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'var(--text-muted)',
                fontSize: '1.1rem',
                '&.Mui-focused': {
                  color: 'var(--neon-cyan)',
                },
              },
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: '1px solid var(--glass-border)',
          pt: 2,
          px: 3,
          pb: 3
        }}>
          <Button
            onClick={handleCancelRename}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRename}
            disabled={!newConversationTitle.trim()}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Conversation Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
          }
        }}
      >
        <DialogTitle sx={{ color: 'var(--text-primary)', borderBottom: '1px solid var(--glass-border)' }}>
          Share AI Conversation
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedConversationForMenu && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--glass-border)' }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--success)', mb: 1 }}>
                Conversation to Share
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                {selectedConversationForMenu.title || 'AI Conversation'}
              </Typography>
              {selectedConversationForMenu.preview && (
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block', mt: 1 }}>
                  {selectedConversationForMenu.preview}
                </Typography>
              )}
            </Box>
          )}

          <TextField
            fullWidth
            label="Search Users"
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              sx: { color: 'var(--text-primary)' }
            }}
            InputLabelProps={{
              sx: { color: 'var(--text-secondary)' }
            }}
          />

          <Box sx={{ maxHeight: '200px', overflow: 'auto', mb: 2 }}>
            {(() => {
              // If there's a search query, show all matching users
              if (userSearchQuery.trim()) {
                return users
                  .filter(user => 
                    user.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                    user.email.toLowerCase().includes(userSearchQuery.toLowerCase())
                  )
                  .map((user) => (
                    <ListItem
                      key={user.id}
                      button
                      onClick={() => setSelectedUser(user)}
                      sx={{
                        bgcolor: selectedUser?.id === user.id ? 'rgba(108, 48, 130, 0.1)' : 'transparent',
                        border: selectedUser?.id === user.id ? '1px solid var(--neon-cyan)' : '1px solid transparent',
                        borderRadius: 'var(--radius-md)',
                        mb: 1,
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.05)'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                            {user.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                            {user.email} • {user.role}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ));
              }
              
              // If no search query, show only the last shared user (if exists)
              if (lastSharedUser) {
                return (
                  <ListItem
                    key={lastSharedUser.id}
                    button
                    onClick={() => setSelectedUser(lastSharedUser)}
                    sx={{
                      bgcolor: selectedUser?.id === lastSharedUser.id ? 'rgba(108, 48, 130, 0.1)' : 'transparent',
                      border: selectedUser?.id === lastSharedUser.id ? '1px solid var(--neon-cyan)' : '1px solid transparent',
                      borderRadius: 'var(--radius-md)',
                      mb: 1,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                          {lastSharedUser.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                          {lastSharedUser.email} • {lastSharedUser.role} • Last shared with
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              }
              
              // If no last shared user and no search, show message
              return (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
                    Start typing to search for users
                  </Typography>
                </Box>
              );
            })()}
          </Box>

          <TextField
            fullWidth
            label="Message (optional)"
            value={shareMessage}
            onChange={(e) => setShareMessage(e.target.value)}
            placeholder="Add a message to accompany the shared conversation..."
            multiline
            rows={2}
            InputProps={{
              sx: { color: 'var(--text-primary)' }
            }}
            InputLabelProps={{
              sx: { color: 'var(--text-secondary)' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid var(--glass-border)' }}>
          <Button
            onClick={() => {
              setShareDialogOpen(false);
              setSelectedUser(null);
              setShareMessage('');
              setUserSearchQuery('');
              setSelectedConversationForMenu(null);
            }}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            Cancel
          </Button>
          <Button
            onClick={handleShareToUser}
            disabled={!selectedUser || sending}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            {sending ? 'Sharing...' : 'Share Conversation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Styled Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            backgroundColor: snackbar.severity === 'warning' 
              ? 'rgba(255, 152, 0, 0.9)' 
              : snackbar.severity === 'error'
              ? 'rgba(244, 67, 54, 0.9)'
              : snackbar.severity === 'success'
              ? 'rgba(70, 130, 48, 0.9)'
              : 'rgba(108, 48, 130, 0.9)',
            color: '#ffffff',
            border: snackbar.severity === 'warning'
              ? '1px solid rgba(255, 152, 0, 0.5)'
              : snackbar.severity === 'error'
              ? '1px solid rgba(244, 67, 54, 0.5)'
              : snackbar.severity === 'success'
              ? '1px solid rgba(70, 130, 48, 0.5)'
              : '1px solid rgba(108, 48, 130, 0.5)',
            borderRadius: 'var(--radius-md)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            '& .MuiAlert-icon': {
              color: '#ffffff'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            backdropFilter: 'blur(10px)',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(5px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'var(--text-primary)', 
          borderBottom: '1px solid var(--glass-border)',
          pb: 2
        }}>
          <Typography variant="h6" sx={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon style={{ fontSize: '1.2rem', color: '#ff4444' }} />
            Remove Shared Conversation
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography sx={{ color: 'var(--text-secondary)' }}>
            Are you sure you want to remove this shared conversation? This will only remove it from your view and won't affect the original conversation.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid var(--glass-border)' }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteSharedConversation}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'delete')}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AIResearchAssistant; 